import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Menu, X, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
const topics = [{
  id: 'ducted',
  title: 'What is a ducted system?'
}, {
  id: 'cassette-vs-wall',
  title: 'Cassette vs wall mounted'
}, {
  id: 'office',
  title: 'Best systems for offices'
}, {
  id: 'retail',
  title: 'Best systems for retail'
}];
export function SystemGuides() {
  const [activeId, setActiveId] = useState<string>('');
  const [isMobileTocOpen, setIsMobileTocOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    // Handle initial hash routing
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => scrollTo(id), 100);
    }
  }, [location.hash]);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -80% 0px'
    });
    topics.forEach(({
      id
    }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileTocOpen(false);
  };
  return <div className="min-h-screen bg-white font-sans text-primary-dark" data-id="element-1873">
      <section className="bg-[#E8F0F8] pt-32 pb-20" data-id="element-1874">
        <div className="container mx-auto px-6" data-id="element-1875">
          <div className="max-w-4xl" data-id="element-1876">
            <div className="flex items-center gap-2 text-sm font-medium text-primary-dark/60 mb-6" data-id="element-1877">
              <Link to="/knowledge-hub" className="hover:text-accent transition-colors" data-id="element-1878">
                Resources
              </Link>
              <ChevronRight className="w-4 h-4" data-id="element-1879" />
              <span className="text-primary-dark" data-id="element-1880">System Guides</span>
            </div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-1881">
              Commercial AC system guides
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-1882">
              Learn about the different types of commercial air conditioning
              systems, compare options, and discover which setup is best suited
              for your specific building and sector.
            </motion.p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 py-16" data-id="element-1883">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-1884">
          <div className="lg:hidden" data-id="element-1885">
            <button onClick={() => setIsMobileTocOpen(!isMobileTocOpen)} className="flex items-center gap-2 text-primary-dark font-medium w-full justify-between bg-[#E8F0F8] p-4 rounded-xl" data-id="element-1886">
              <span data-id="element-1887">Table of contents</span>
              {isMobileTocOpen ? <X className="w-5 h-5" data-id="element-1888" /> : <Menu className="w-5 h-5" data-id="element-1889" />}
            </button>
            {isMobileTocOpen && <div className="mt-2 bg-white border border-primary-dark/10 rounded-xl p-4 shadow-lg" data-id="element-1890">
                <nav className="flex flex-col gap-3" data-id="element-1891">
                  {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/70 hover:text-primary-dark'}`} data-id="element-1892">
                      {topic.title}
                    </button>)}
                </nav>
              </div>}
          </div>

          <aside className="hidden lg:block w-64 shrink-0" data-id="element-1893">
            <div className="sticky top-32" data-id="element-1894">
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary-dark/50 mb-6" data-id="element-1895">
                Contents
              </h3>
              <nav className="flex flex-col gap-4 border-l-2 border-primary-dark/10 pl-4" data-id="element-1896">
                {topics.map(topic => <button key={topic.id} onClick={() => scrollTo(topic.id)} className={`text-left text-sm font-medium transition-colors relative ${activeId === topic.id ? 'text-accent' : 'text-primary-dark/60 hover:text-primary-dark'}`} data-id="element-1897">
                    {activeId === topic.id && <motion.div layoutId="activeToc" className="absolute -left-[18px] top-1/2 -translate-y-1/2 w-0.5 h-full bg-accent" data-id="element-1898" />}
                    {topic.title}
                  </button>)}
              </nav>
            </div>
          </aside>

          <main className="flex-1 max-w-3xl prose prose-lg prose-headings:font-serif prose-headings:text-primary-dark prose-p:text-primary-dark/80 prose-a:text-accent prose-li:text-primary-dark/80" data-id="element-1899">
            {/* Topic 1 */}
            <section id="ducted" className="mb-20 scroll-mt-32" data-id="element-1900">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1901">
                What is a ducted air conditioning system?
              </h2>
              <p data-id="element-1902">
                A ducted air conditioning system is the ultimate solution for
                discreet, whole-building climate control. Unlike wall-mounted or
                cassette units where the machinery is visible in the room, a
                ducted system hides the main fan coil unit within a suspended
                ceiling or roof void.
              </p>
              <p data-id="element-1903">
                Conditioned air is then distributed through a network of
                concealed insulated ducts and delivered into the room via subtle
                grilles or diffusers. These grilles can be designed to match the
                interior aesthetics, making the system virtually invisible.
              </p>
              <p data-id="element-1904">
                Because the mechanical components are hidden away from the
                occupied space, ducted systems are exceptionally quiet. They are
                the preferred choice for high-end offices, luxury retail spaces,
                and premium hospitality venues where aesthetics and acoustics
                are paramount.
              </p>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-1905">
                <h4 className="text-xl font-serif mb-4" data-id="element-1906">
                  Considering a ducted system?
                </h4>
                <p className="mb-6 text-base" data-id="element-1907">
                  Learn more about the benefits and technical requirements.
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-1908">
                  <Link to="/services/ducted" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1909">
                    See the related system <ArrowRight className="w-4 h-4" data-id="element-1910" />
                  </Link>
                  <Link to="/contact" className="inline-flex items-center gap-2 bg-white hover:bg-white/80 text-primary-dark px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1911">
                    Talk to us about the right setup
                  </Link>
                </div>
              </div>
            </section>

            {/* Topic 2 */}
            <section id="cassette-vs-wall" className="mb-20 scroll-mt-32" data-id="element-1912">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1913">
                Cassette vs wall mounted air conditioning
              </h2>
              <p data-id="element-1914">
                When choosing between cassette and wall-mounted air conditioning
                for a commercial space, the decision usually comes down to room
                size, ceiling type, and budget.
              </p>
              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1915">
                Cassette Systems
              </h3>
              <p data-id="element-1916">
                Cassette units are mounted into a suspended ceiling, with only a
                flush fascia panel visible. They distribute air in four
                directions (360 degrees in modern units), providing very even
                temperature control across large, open-plan areas. They are
                ideal for open offices and retail stores but require a ceiling
                void to house the unit.
              </p>
              <h3 className="text-2xl font-serif mt-8 mb-4" data-id="element-1917">
                Wall Mounted Systems
              </h3>
              <p data-id="element-1918">
                Wall-mounted units are affixed high up on a wall. They blow air
                in a single direction (outwards and downwards). They are highly
                cost-effective, quick to install, and perfect for individual
                rooms, server rooms, or spaces without a suspended ceiling.
                However, in very large open spaces, a single wall unit may
                struggle to distribute air evenly to the far corners.
              </p>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-1919">
                <h4 className="text-xl font-serif mb-4" data-id="element-1920">
                  Compare your options
                </h4>
                <p className="mb-6 text-base" data-id="element-1921">
                  Explore our detailed system pages to find the perfect fit.
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-1922">
                  <Link to="/services/cassette" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1923">
                    See cassette systems <ArrowRight className="w-4 h-4" data-id="element-1924" />
                  </Link>
                  <Link to="/services/wall-mounted" className="inline-flex items-center gap-2 bg-white hover:bg-white/80 text-primary-dark px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1925">
                    See wall mounted systems <ArrowRight className="w-4 h-4" data-id="element-1926" />
                  </Link>
                </div>
              </div>
            </section>

            {/* Topic 3 */}
            <section id="office" className="mb-20 scroll-mt-32" data-id="element-1927">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1928">
                Which commercial air conditioning system is best for an office?
              </h2>
              <p data-id="element-1929">
                The "best" system for an office depends entirely on the office
                layout. Modern offices typically employ a mix of systems to
                achieve optimal comfort and efficiency.
              </p>
              <ul className="list-disc pl-6 mb-8 space-y-2" data-id="element-1930">
                <li data-id="element-1931">
                  <strong data-id="element-1932">Open-plan areas:</strong> Cassette systems or ducted
                  systems are best here. They provide even, draft-free air
                  distribution across large spaces where multiple people are
                  working.
                </li>
                <li data-id="element-1933">
                  <strong data-id="element-1934">Meeting rooms:</strong> Wall-mounted units or small
                  ducted units work well, allowing occupants to quickly adjust
                  the temperature for a specific meeting without affecting the
                  rest of the floor.
                </li>
                <li data-id="element-1935">
                  <strong data-id="element-1936">Server rooms:</strong> Dedicated wall-mounted units
                  are the standard, providing reliable, 24/7 cooling independent
                  of the main office system.
                </li>
                <li data-id="element-1937">
                  <strong data-id="element-1938">Premium boardrooms:</strong> Ducted systems are
                  preferred for their near-silent operation and invisible
                  aesthetics.
                </li>
              </ul>
              <p data-id="element-1939">
                For larger office buildings, these individual indoor units are
                usually connected to a central VRV or VRF outdoor system, which
                allows for simultaneous heating and cooling in different parts
                of the building while maximizing energy efficiency.
              </p>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-1940">
                <h4 className="text-xl font-serif mb-4" data-id="element-1941">
                  Need an office HVAC design?
                </h4>
                <p className="mb-6 text-base" data-id="element-1942">
                  Our engineers design bespoke systems for optimal workplace
                  comfort.
                </p>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1943">
                  Talk to us about the right setup{' '}
                  <ArrowRight className="w-4 h-4" data-id="element-1944" />
                </Link>
              </div>
            </section>

            {/* Topic 4 */}
            <section id="retail" className="mb-20 scroll-mt-32" data-id="element-1945">
              <h2 className="text-3xl font-serif mb-6" data-id="element-1946">
                Which air conditioning system is best for retail?
              </h2>
              <p data-id="element-1947">
                Retail environments present unique HVAC challenges: high
                footfall, constantly opening doors, dense lighting heat, and the
                need to keep customers comfortable enough to linger.
              </p>
              <p data-id="element-1948">
                <strong data-id="element-1949">Cassette systems</strong> are the most popular choice
                for standard retail spaces. They fit neatly into standard
                suspended ceilings, distribute air evenly across the shop floor,
                and don't take up valuable wall or floor space that could be
                used for merchandising.
              </p>
              <p data-id="element-1950">
                <strong data-id="element-1951">Ducted systems</strong> are heavily favoured by high-end
                boutiques, luxury brands, and modern industrial-chic spaces. The
                ductwork can either be completely hidden above a ceiling for a
                pristine look, or left exposed (spiral ducting) to contribute to
                an urban aesthetic.
              </p>
              <p data-id="element-1952">
                Retail spaces also frequently require{' '}
                <strong data-id="element-1953">over-door heaters or air curtains</strong> to create a
                thermal barrier at the entrance, preventing conditioned air from
                escaping when doors are open.
              </p>

              <div className="bg-[#E8F0F8] p-8 rounded-2xl my-8" data-id="element-1954">
                <h4 className="text-xl font-serif mb-4" data-id="element-1955">
                  Upgrading your retail space?
                </h4>
                <p className="mb-6 text-base" data-id="element-1956">
                  Ensure your climate control enhances the customer experience.
                </p>
                <div className="flex flex-wrap gap-4" data-id="element-1957">
                  <Link to="/services/cassette" className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1958">
                    See cassette systems <ArrowRight className="w-4 h-4" data-id="element-1959" />
                  </Link>
                  <Link to="/contact" className="inline-flex items-center gap-2 bg-white hover:bg-white/80 text-primary-dark px-6 py-3 rounded-full text-sm font-medium transition-colors" data-id="element-1960">
                    Talk to us about the right setup
                  </Link>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>;
}